import { Directive, ElementRef, HostListener, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  isUnparseableCompleteDate,
  normalizeTypedDate,
  parseDateOnly
} from '../CommonFunctions/date-utils';

/**
 * Makes every bsDatepicker in the portal safe to feed and safe to type into.
 *
 * ngx-bootstrap parses whatever it is given with the picker's own
 * `dateInputFormat` ('MMM-DD-YYYY' everywhere here) and chronos does NOT fail
 * loudly when the text does not match the mask - it quietly substitutes
 * defaults. '03-04-2005' comes back as Jan-04-2005, because 'MMM' cannot read
 * '03' so the month falls back to January while DD and YYYY still match. A
 * wrong-but-valid date is worse than an error: it saves silently.
 *
 * Two guards, covering the two ways a value gets in:
 *
 *  1. Programmatically (patchValue/setValue/ngModel, from API data): the
 *     control value is coerced to a real Date before the picker ever sees it.
 *     This holds no matter which code path did the patching.
 *  2. Typed by hand: the input's text is rewritten to 'MMM-DD-YYYY' on the
 *     `input` event, which always fires before the `change` event the picker
 *     listens to.
 *
 * The selector matches ngx-bootstrap's own, so every picker is covered without
 * a single template change.
 */
@Directive({
  selector: 'input[bsDatepicker]'
})
export class DatepickerTypedInputDirective implements OnInit, OnDestroy {

  private sub?: Subscription;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() @Self() private ngControl: NgControl
  ) { }

  ngOnInit(): void {
    // The host form directive may not have built its control yet, so settle
    // first and then coerce whatever is there and whatever arrives later.
    Promise.resolve().then(() => {
      const control = this.ngControl?.control;
      if (!control) {
        return;
      }
      this.coerce(control.value);
      this.sub = control.valueChanges.subscribe(value => this.coerce(value));
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Replaces a date-ish string with the equivalent local-midnight Date. */
  private coerce(value: any): void {
    const control = this.ngControl?.control;
    if (!control) {
      return;
    }
    if (value === null || value === undefined || value === '' || value instanceof Date) {
      return;
    }
    const parsed = parseDateOnly(value);
    if (!parsed) {
      return;
    }
    control.setValue(parsed, { emitEvent: false });
  }

  @HostListener('input')
  onInput(): void {
    const input = this.el.nativeElement;
    const normalized = normalizeTypedDate(input.value);
    if (normalized) {
      if (normalized !== input.value) {
        input.value = normalized;
      }
      return;
    }
    // A finished-looking date that names no real day: clear it, so the field
    // reports "required" instead of quietly becoming an unrelated valid date.
    if (isUnparseableCompleteDate(input.value)) {
      input.value = '';
    }
  }

}
